/** @file node.set: merges fields into a node (parent and mirror have their own commands). */
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import type { Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { NODE_SHAPE } from '@zilo/pose3d-format/validate/shapes/nodeShape';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { readInput } from '../validate/readInput';
import { requireNode } from './requireNode';

const FIELDS: Shape = {
  required: {},
  optional: Object.fromEntries(Object.entries({ ...NODE_SHAPE.required, ...NODE_SHAPE.optional }).filter(([k]) => k !== 'parent' && k !== 'mirror')),
};

/**
 * `node.set { name, fields }` → `{}`: `fields` may hold any node field but `parent` and `mirror` (e.g. `frameAnims`,
 * `originOffset`). Inverse: `node.set` with the previous values when every field existed, else P.
 */
export const nodeSetCommand: CommandDef<'node.set'> = {
  name: 'node.set',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'fields']);
    return { name: r.str('name'), fields: expectShape(r.raw('fields'), r.at('fields'), FIELDS) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const node = requireNode(draft.puppet, input.name, '/input/name');
    const keys = Object.keys(input.fields) as (keyof PuppetNode)[];
    const inverse = keys.every((k) => node[k] !== undefined)
      ? [{ name: 'node.set' as const, input: { name: input.name, fields: structuredClone(Object.fromEntries(keys.map((k) => [k, node[k]]))) } }]
      : puppetInverse(draft);
    Object.assign(node, structuredClone(input.fields));
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse };
  },
};
